import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { X, Eye, Trash2, Volume2, VolumeX, Heart, MessageCircle, BarChart3, Link as LinkIcon, Pin, Check } from 'lucide-react';
import { SwipaSend as Send, SwipaLoader } from '@/icons';
import { storiesApi, StoryGroup, StoryViewer as StoryViewerType, StoryComment } from '@/api/stories';
import { friendsApi, FriendEntry } from '@/api/friends';
import { apiClient } from '@/api/client';
import { useAuthStore } from '@/store/authStore';
import { useSignedMediaUrl } from '@/hooks/useSignedMediaUrl';
import Avatar from '@/components/ui/Avatar';
import { useHlsVideo } from '@/hooks/useHlsVideo';
import StoryAnalyticsModal from './StoryAnalyticsModal';

interface Props {
  group: StoryGroup;
  onClose: () => void;
}

const STORY_DURATION_MS = 5000;
const TICK_MS = 100;

export default function StoryViewer({ group, onClose }: Props) {
  const { user, accessToken } = useAuthStore();
  const isOwn = group.author.id === user?.id;

  // Local copy so we can remove stories (e.g. after delete) without the parent
  // refetching the whole feed. Always hydrate from the prop when the group changes.
  const [stories, setStories] = useState(group.stories);
  useEffect(() => { setStories(group.stories); }, [group.stories]);

  const [index, setIndex] = useState(() => {
    const firstUnviewed = group.stories.findIndex((s) => !s.viewed);
    return firstUnviewed >= 0 ? firstUnviewed : 0;
  });

  const story = stories[index];
  const total = stories.length;
  const isVideo = story?.mimeType?.startsWith('video/');

  // Like state (local, updated optimistically)
  const [isLiked, setIsLiked] = useState(story?.isLiked ?? false);
  const [likeCount, setLikeCount] = useState(story?.likeCount ?? 0);
  const [likeAnim, setLikeAnim] = useState(false);

  // Panels
  const [showViewers, setShowViewers] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [analyticsFor, setAnalyticsFor] = useState<string | null>(null);
  const [copiedLink, setCopiedLink] = useState(false);

  // Video loading overlay — true while <video> buffers, false once it plays.
  // Reset on each story change so the SwipaLoader shows for the next clip too.
  const [videoLoading, setVideoLoading] = useState(true);
  useEffect(() => { setVideoLoading(true); }, [index]);

  // Video ref — МUST be declared before useHlsVideo below reads it.
  // (If moved down we hit TDZ: minifier produced "Cannot access 'Ee'
  // before initialization" on prod.)
  const videoRef = useRef<HTMLVideoElement>(null);

  // TikTok-style adaptive streaming.  useHlsVideo HEADs the manifest first;
  // if the transcode is ready it streams via HLS (native on Safari, hls.js
  // elsewhere).  On 404 or fatal error it falls back to plain MP4 /download.
  // P1 — signed URLs (manifest + fallback). useHlsVideo тригерится когда
  // оба URL'а готовы; пока signedSrc null рендерится pre-load placeholder.
  const signedManifest = useSignedMediaUrl(
    story?.mediaFileId && isVideo ? story.mediaFileId : null,
    'playlist.m3u8',
  );
  const signedFallback = useSignedMediaUrl(story?.mediaFileId ?? null, 'download');
  useHlsVideo({
    videoRef,
    manifestUrl: signedManifest,
    fallbackUrl: signedFallback ?? '',
    resetKey: story?.id,
  });

  // 18+ gate — for ADULT content, blur the media until the viewer either
  // has `showAdult=true` on their profile or explicitly confirms their age
  // here.  Confirmation is persisted server-side via /api/users/adult-consent.
  const [adultConfirmed, setAdultConfirmed] = useState(
    () => !!(useAuthStore.getState().user?.showAdult),
  );
  useEffect(() => { setAdultConfirmed(!!user?.showAdult); }, [user?.showAdult]);

  const isAdult = story?.contentRating === 'ADULT';
  const isSuggestive = story?.contentRating === 'SUGGESTIVE';
  const needsAgeGate = isAdult && !adultConfirmed;
  // Soft blur for SUGGESTIVE — always on, pausable via tap-to-reveal.
  const [suggestiveRevealed, setSuggestiveRevealed] = useState(false);
  useEffect(() => { setSuggestiveRevealed(false); }, [index]);

  const confirmAdult = useCallback(async () => {
    try {
      await apiClient.post('/api/users/adult-consent', { show: true });
      useAuthStore.setState((s) => ({
        user: s.user ? { ...s.user, showAdult: true, ageVerifiedAt: new Date().toISOString() } : s.user,
      }));
      setAdultConfirmed(true);
    } catch (e) {
      console.warn('[StoryViewer] adult consent failed', e);
    }
  }, []);

  // Viewers
  const [viewers, setViewers] = useState<StoryViewerType[]>([]);

  // Comments
  const [comments, setComments] = useState<StoryComment[]>([]);
  const [commentInput, setCommentInput] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const commentEndRef = useRef<HTMLDivElement>(null);

  // Share
  const [friends, setFriends] = useState<FriendEntry[]>([]);
  const [sentTo, setSentTo] = useState<Set<string>>(new Set());

  // Video (videoRef уже объявлен выше useHlsVideo)
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);

  const paused = showComments || showViewers || showShare;

  // Sync like state when story changes
  useEffect(() => {
    setIsLiked(story?.isLiked ?? false);
    setLikeCount(story?.likeCount ?? 0);
    setShowComments(false);
    setShowShare(false);
    setSentTo(new Set());
  }, [index, story?.id]);

  // Mark as viewed
  useEffect(() => {
    if (story && !story.viewed) {
      storiesApi.markViewed(story.id).catch(() => {});
      story.viewed = true;
    }
  }, [story]);

  // Compute URLs for the next 2 stories — we render hidden <video preload="metadata">
  // elements for them so the browser fetches just the moov atom + first segment
  // (tens of KB) instead of the whole file. When the user advances, playback
  // starts instantly via HTTP Range requests.
  const preloadUrls = useMemo(() => {
    const tokenQuery = accessToken ? `?token=${accessToken}` : '';
    const urls: Array<{ id: string; url: string; isVideo: boolean }> = [];
    for (let offset = 1; offset <= 2; offset++) {
      const next = stories[index + offset];
      if (!next) break;
      urls.push({
        id: next.id,
        url: `/api/media/${next.mediaFileId}/download${tokenQuery}`,
        isVideo: !!next.mimeType?.startsWith('video/'),
      });
    }
    return urls;
  }, [index, stories, accessToken]);

  // NOTE: no manual <video> cleanup here. The `key={story.id}` attribute on
  // the video element makes React fully unmount the old element when the
  // story changes — the browser then releases its resources automatically.
  // A manual cleanup that touches videoRef.current runs AFTER React has
  // already pointed the ref at the NEW element, so it would blank out the
  // NEW video's src and leave the viewer with a black screen.

  // Auto-advance timer
  useEffect(() => {
    if (isVideo || paused) { setProgress(isVideo ? 0 : progress); return; }
    setProgress(0);
    const interval = setInterval(() => {
      setProgress((p) => {
        if (p >= 100) {
          if (index < total - 1) setIndex((i) => i + 1);
          else onClose();
          return 0;
        }
        return p + (TICK_MS / STORY_DURATION_MS) * 100;
      });
    }, TICK_MS);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, total, onClose, isVideo, paused]);

  // NOTE: do NOT unmute here. Browsers' autoplay policy allows muted autoplay,
  // but unmuting without a user gesture pauses the video. User must tap the
  // mute icon to enable sound.
  const onVideoPlay = useCallback(() => {}, []);

  // Throttle-ping backend каждые 2с, чтобы автор видел retention.
  const lastProgressPingRef = useRef(0);
  const onVideoTimeUpdate = useCallback(() => {
    const vid = videoRef.current;
    if (!vid || !vid.duration) return;
    const pct = (vid.currentTime / vid.duration) * 100;
    setProgress(pct);

    const now = performance.now();
    if (story?.id && now - lastProgressPingRef.current > 2000) {
      lastProgressPingRef.current = now;
      storiesApi.updateProgress(story.id, vid.currentTime, pct).catch(() => {/* silent */});
    }
  }, [story?.id]);

  const onVideoEnded = useCallback(() => {
    if (index < total - 1) setIndex((i) => i + 1);
    else onClose();
  }, [index, total, onClose]);

  const goNext = useCallback(() => {
    if (index < total - 1) setIndex((i) => i + 1);
    else onClose();
  }, [index, total, onClose]);

  const goPrev = useCallback(() => {
    if (index > 0) setIndex((i) => i - 1);
  }, [index]);

  const handleKey = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') { if (showComments || showShare || showViewers) { setShowComments(false); setShowShare(false); setShowViewers(false); } else onClose(); }
    // TikTok-style: ↑/↓ navigate between stories (primary).
    // ←/→ kept for Instagram-style desktop tap-zone parity.
    if (!paused) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        goNext();
      }
      if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      }
    }
  }, [onClose, goNext, goPrev, paused, showComments, showShare, showViewers]);

  useEffect(() => {
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', handleKey); document.body.style.overflow = ''; };
  }, [handleKey]);

  // ── TikTok-style vertical swipe navigation ────────────────────────────
  // - Touch: drag up/down, release → snap to next/prev if |dy| > threshold.
  //   Rubber-band resistance at list edges (first/last story).
  // - Mouse wheel: deltaY → next/prev with debounce (so one trackpad flick
  //   doesn't skip 5 stories).
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const touchStartY = useRef(0);
  const lastWheelAt = useRef(0);
  const panelsOpen = showComments || showShare || showViewers;

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    if (panelsOpen) return;
    touchStartY.current = e.touches[0].clientY;
    setIsDragging(true);
  }, [panelsOpen]);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (panelsOpen || !isDragging) return;
    const dy = e.touches[0].clientY - touchStartY.current;
    // Rubber-band at list edges — resist the drag so user feels the wall.
    const atTopEdge = index === 0 && dy > 0;
    const atBottomEdge = index === total - 1 && dy < 0;
    setDragY(atTopEdge || atBottomEdge ? dy * 0.25 : dy);
  }, [isDragging, index, total, panelsOpen]);

  const onTouchEnd = useCallback(() => {
    if (panelsOpen) return;
    setIsDragging(false);
    const threshold = 80;
    if (dragY < -threshold) {
      if (index < total - 1) setIndex((i) => i + 1);
      else onClose();
    } else if (dragY > threshold) {
      if (index > 0) setIndex((i) => i - 1);
      else onClose();
    }
    setDragY(0);
  }, [dragY, index, total, onClose, panelsOpen]);

  const onWheel = useCallback((e: React.WheelEvent) => {
    if (panelsOpen) return;
    const now = Date.now();
    // Debounce — trackpad sends a burst of wheel events per flick.
    if (now - lastWheelAt.current < 400) return;
    if (Math.abs(e.deltaY) < 20) return;
    lastWheelAt.current = now;
    if (e.deltaY > 0) goNext();
    else goPrev();
  }, [goNext, goPrev, panelsOpen]);

  const handleDelete = async () => {
    const deletedId = story.id;
    try {
      await storiesApi.delete(deletedId);
    } catch (e: any) {
      // 404 means the story is already gone from the DB (expired, deleted
      // elsewhere, stale feed cache). Treat that as success and keep going.
      if (e?.response?.status !== 404) {
        console.error('[StoryViewer] delete failed', e);
        return;
      }
    }

    // Drop from local state so the deleted story disappears immediately.
    setStories((prev) => {
      const next = prev.filter((s) => s.id !== deletedId);
      if (next.length === 0) {
        onClose();
      } else if (index >= next.length) {
        setIndex(next.length - 1);
      }
      return next;
    });
  };

  const loadViewers = async () => {
    const data = await storiesApi.getViewers(story.id);
    setViewers(data);
    setShowViewers(true);
  };

  const handleLike = async () => {
    const next = !isLiked;
    setIsLiked(next);
    setLikeCount((c) => c + (next ? 1 : -1));
    if (next) { setLikeAnim(true); setTimeout(() => setLikeAnim(false), 400); }
    try {
      const res = await storiesApi.toggleLike(story.id);
      setIsLiked(res.liked);
      setLikeCount(res.likeCount);
    } catch {
      setIsLiked(!next);
      setLikeCount((c) => c + (next ? -1 : 1));
    }
  };

  const openComments = async () => {
    setShowComments(true);
    try {
      const data = await storiesApi.getComments(story.id);
      setComments(data);
    } catch { /* ignore */ }
  };

  const submitComment = async () => {
    const text = commentInput.trim();
    if (!text || commentLoading) return;
    setCommentLoading(true);
    try {
      const c = await storiesApi.addComment(story.id, text);
      setComments((prev) => [...prev, c]);
      setCommentInput('');
      story.commentCount = (story.commentCount ?? 0) + 1;
      setTimeout(() => commentEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    } catch { /* ignore */ }
    setCommentLoading(false);
  };

  const deleteComment = async (commentId: string) => {
    try {
      await storiesApi.deleteComment(story.id, commentId);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
      story.commentCount = Math.max(0, (story.commentCount ?? 1) - 1);
    } catch { /* ignore */ }
  };

  const openShare = async () => {
    setShowShare(true);
    if (friends.length === 0) {
      try {
        const data = await friendsApi.list();
        setFriends(data);
      } catch { /* ignore */ }
    }
  };

  const sendToDm = async (recipientId: string) => {
    if (sentTo.has(recipientId)) return;
    try {
      await apiClient.post(`/api/messages/dm/${recipientId}`, {
        content: '',
        mediaFileId: story.mediaFileId,
      });
      setSentTo((s) => new Set(s).add(recipientId));
    } catch { /* ignore */ }
  };

  if (!story) return null;

  const timeAgo = (() => {
    const diff = Date.now() - new Date(story.createdAt).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins} мин`;
    return `${Math.floor(mins / 60)} ч`;
  })();

  const commentCount = story.commentCount ?? comments.length;

  return (
    <div
      className="fixed inset-0 z-[70] bg-black flex items-center justify-center select-none touch-none"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onWheel={onWheel}
      style={{
        transform: `translateY(${dragY}px)`,
        transition: isDragging ? 'none' : 'transform 280ms cubic-bezier(0.32, 0.72, 0, 1)',
        willChange: 'transform',
      }}>
      {/* Progress bars */}
      <div className="absolute top-2 left-4 right-4 flex gap-1 z-20">
        {stories.map((_, i) => (
          <div key={i} className="flex-1 h-0.5 bg-white/30 rounded-full overflow-hidden">
            <div
              className="h-full bg-white rounded-full transition-all duration-100"
              style={{ width: i < index ? '100%' : i === index ? `${progress}%` : '0%' }}
            />
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="absolute top-6 left-4 right-4 flex items-center gap-3 z-20">
        <Avatar username={group.author.username} src={group.author.avatarUrl} size="sm" />
        <div className="flex-1 min-w-0">
          <span className="text-white text-sm font-medium">{group.author.displayName}</span>
          <span className="text-white/60 text-xs ml-2">{timeAgo}</span>
        </div>
        <div className="flex items-center gap-2">
          {isVideo && (
            <button onClick={() => { setIsMuted((m) => !m); if (videoRef.current) videoRef.current.muted = !isMuted; }} className="p-1.5 text-white/70 hover:text-white">
              {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </button>
          )}
          {isOwn && (
            <>
              <button
                onClick={() => story && setAnalyticsFor(story.id)}
                className="p-1.5 text-white/70 hover:text-white"
                title="Аналитика"
              >
                <BarChart3 size={18} />
              </button>
              <button onClick={loadViewers} className="p-1.5 text-white/70 hover:text-white">
                <Eye size={18} />
              </button>
              <button onClick={handleDelete} className="p-1.5 text-white/70 hover:text-red-400">
                <Trash2 size={18} />
              </button>
            </>
          )}
          {/* Share-link — копирует /s/:id в буфер */}
          <button
            onClick={async () => {
              if (!story) return;
              const url = `${window.location.origin}/s/${story.id}`;
              try {
                await navigator.clipboard.writeText(url);
                setCopiedLink(true);
                setTimeout(() => setCopiedLink(false), 1500);
                storiesApi.recordShare(story.id).catch(() => {/* silent */});
              } catch { /* ignore */ }
            }}
            className="p-1.5 text-white/70 hover:text-white"
            title="Скопировать ссылку"
          >
            {copiedLink ? <Check size={18} /> : <LinkIcon size={18} />}
          </button>
          <button onClick={onClose} className="p-1.5 text-white/70 hover:text-white">
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Story media.  ADULT → hard age gate (full black + confirm button).
          SUGGESTIVE → soft blur, tap to reveal for this story only.  Logic
          for blur CSS is applied to the media element via `mediaBlurClass`. */}
      {(() => {
        const applyBlur =
          (isAdult && !adultConfirmed) ||
          (isSuggestive && !suggestiveRevealed);
        const mediaBlurClass = applyBlur ? 'blur-2xl scale-110' : '';
        return (
          <>
            {isVideo ? (
              <>
                <video
                  ref={videoRef}
                  key={story.id}
                  className={`max-w-full max-h-full object-contain transition-all duration-300 ${mediaBlurClass}`}
                  autoPlay muted playsInline preload="auto"
                  onPlay={(e) => { setVideoLoading(false); onVideoPlay(); void e; }}
                  onPlaying={() => setVideoLoading(false)}
                  onLoadedData={() => setVideoLoading(false)}
                  onWaiting={() => setVideoLoading(true)}
                  onTimeUpdate={onVideoTimeUpdate}
                  onEnded={onVideoEnded}
                  onError={() => goNext()}
                />
                {videoLoading && !applyBlur && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                    <SwipaLoader size={72} color="#ffffff" />
                  </div>
                )}
              </>
            ) : (
              signedFallback ? (
                <img
                  src={signedFallback}
                  alt=""
                  className={`max-w-full max-h-full object-contain transition-all duration-300 ${mediaBlurClass}`}
                  onError={() => goNext()}
                />
              ) : (
                <div className="w-32 h-32 rounded-full border-4 border-white/20 border-t-white/80 animate-spin" />
              )
            )}
          </>
        );
      })()}

      {/* 18+ hard gate overlay */}
      {needsAgeGate && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/60 backdrop-blur-xl">
          <div className="flex flex-col items-center gap-4 px-6 py-8 rounded-2xl bg-black/70 border border-white/10 max-w-xs text-center">
            <div className="w-14 h-14 rounded-full bg-rose-500/20 flex items-center justify-center">
              <span className="text-rose-300 text-2xl font-bold">18+</span>
            </div>
            <h3 className="text-white font-semibold text-lg">Контент для взрослых</h3>
            <p className="text-white/70 text-sm">
              Этот материал может содержать откровенные сцены.
              Подтверди, что тебе 18 и более лет.
            </p>
            <button
              onClick={confirmAdult}
              className="w-full px-5 py-2.5 rounded-lg bg-white text-black font-semibold text-sm hover:bg-white/90 transition-colors"
            >
              Мне 18+, показать
            </button>
            <button
              onClick={onClose}
              className="w-full px-5 py-2 rounded-lg bg-transparent text-white/70 text-sm hover:text-white"
            >
              Закрыть
            </button>
          </div>
        </div>
      )}

      {/* Suggestive soft-blur — tap to reveal this story */}
      {isSuggestive && !suggestiveRevealed && !needsAgeGate && (
        <button
          onClick={() => setSuggestiveRevealed(true)}
          className="absolute inset-0 z-20 flex items-center justify-center bg-black/10"
          aria-label="Показать содержимое"
        >
          <div className="px-4 py-2 rounded-full bg-black/60 text-white/90 text-xs font-medium backdrop-blur-sm">
            Нажмите, чтобы показать
          </div>
        </button>
      )}

      {/* Caption */}
      {story.caption && (
        <div
          className="absolute left-4 right-4 z-20"
          style={{ bottom: 'calc(5rem + env(safe-area-inset-bottom, 0px))' }}
        >
          <p className="text-white text-sm bg-black/50 rounded-lg px-3 py-2 backdrop-blur-sm">
            {story.caption}
          </p>
        </div>
      )}

      {/* Bottom action bar — keep above safe-area / mobile browser chrome */}
      <div
        className="absolute left-4 right-4 z-20 flex items-center justify-end gap-5"
        style={{ bottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))' }}
      >
        {/* Like */}
        <button onClick={handleLike} className="flex flex-col items-center gap-0.5 select-none">
          <Heart
            size={28}
            className={`transition-transform ${likeAnim ? 'scale-125' : 'scale-100'} ${isLiked ? 'fill-red-500 text-red-500' : 'text-white'}`}
          />
          {likeCount > 0 && <span className="text-white text-xs">{likeCount}</span>}
        </button>

        {/* Comments */}
        <button onClick={openComments} className="flex flex-col items-center gap-0.5 select-none">
          <MessageCircle size={28} className="text-white" />
          {commentCount > 0 && <span className="text-white text-xs">{commentCount}</span>}
        </button>

        {/* Share / forward */}
        <button onClick={openShare} className="flex flex-col items-center gap-0.5 select-none">
          <Send size={26} className="text-white" />
        </button>
      </div>

      {/* Preload upcoming stories — metadata only (~100KB for video, a few KB for img).
          When user taps next, the <video>/<img> above reuses these cached ranges
          and playback starts instantly. */}
      <div className="hidden" aria-hidden="true">
        {preloadUrls.map((p) =>
          p.isVideo
            ? <video key={p.id} src={p.url} preload="metadata" muted playsInline />
            : <img key={p.id} src={p.url} alt="" />
        )}
      </div>

      {/* Nav zones (avoid bottom bar) */}
      <div className="absolute top-16 bottom-20 left-0 w-1/3 z-10 cursor-pointer" onClick={goPrev} />
      <div className="absolute top-16 bottom-20 right-0 w-1/3 z-10 cursor-pointer" onClick={goNext} />

      {/* ── Viewers sheet ── */}
      {showViewers && (
        <div className="absolute inset-0 bg-black/80 z-30 flex items-end justify-center" onClick={() => setShowViewers(false)}>
          <div className="bg-surface-900 rounded-t-2xl w-full max-w-lg max-h-[60vh] overflow-y-auto p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-surface-100 font-semibold text-sm">Просмотры ({viewers.length})</h3>
              <button onClick={() => setShowViewers(false)} className="text-surface-400"><X size={18} /></button>
            </div>
            {viewers.length === 0 && <p className="text-surface-500 text-sm text-center py-4">Пока никто не смотрел</p>}
            {viewers.map((v) => (
              <div key={v.viewer.id} className="flex items-center gap-3 py-2">
                <Avatar username={v.viewer.username} src={v.viewer.avatarUrl} size="sm" />
                <div>
                  <p className="text-surface-100 text-sm">{v.viewer.displayName}</p>
                  <p className="text-surface-500 text-xs font-mono">@{v.viewer.username}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Comments sheet ── */}
      {showComments && (
        <div className="absolute inset-0 bg-black/60 z-30 flex items-end justify-center" onClick={() => setShowComments(false)}>
          <div className="bg-surface-900 rounded-t-2xl w-full max-w-lg flex flex-col max-h-[70vh]" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-surface-700">
              <h3 className="text-surface-100 font-semibold text-sm">Комментарии</h3>
              <button onClick={() => setShowComments(false)} className="text-surface-400"><X size={18} /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-2 space-y-3 min-h-[120px]">
              {comments.length === 0 && (
                <p className="text-surface-500 text-sm text-center py-6">Комментариев пока нет</p>
              )}
              {comments.map((c) => (
                <div key={c.id} className="flex items-start gap-2 group">
                  <Avatar username={c.author.username} src={c.author.avatarUrl} size="xs" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="text-surface-300 text-xs font-semibold">{c.author.displayName}</span>
                      {c.isPinned && (
                        <span className="text-[10px] text-brand-400 inline-flex items-center gap-0.5">
                          <Pin size={10} /> Закреплён
                        </span>
                      )}
                    </div>
                    <span className="text-surface-100 text-sm break-words">{c.content}</span>
                  </div>
                  {/* Like */}
                  <button
                    onClick={async () => {
                      try {
                        const res = await storiesApi.toggleCommentLike(c.id);
                        setComments((prev) => prev.map((x) => x.id === c.id ? { ...x, likeCount: res.likeCount } : x));
                      } catch {/* ignore */}
                    }}
                    className="text-surface-500 hover:text-red-400 shrink-0 flex items-center gap-0.5 text-xs"
                    title="Лайк комментария"
                  >
                    <Heart size={12} />
                    {c.likeCount > 0 && <span>{c.likeCount}</span>}
                  </button>
                  {/* Pin (только автору стори) */}
                  {isOwn && (
                    <button
                      onClick={async () => {
                        try {
                          const res = await storiesApi.toggleCommentPin(c.id);
                          setComments((prev) => prev.map((x) => x.id === c.id ? { ...x, isPinned: res.pinned } : x));
                        } catch (e: any) {
                          alert(e?.response?.data?.message ?? 'Не удалось закрепить');
                        }
                      }}
                      className={`opacity-0 group-hover:opacity-100 shrink-0 ${c.isPinned ? 'text-brand-400' : 'text-surface-500 hover:text-brand-400'}`}
                      title={c.isPinned ? 'Открепить' : 'Закрепить'}
                    >
                      <Pin size={14} />
                    </button>
                  )}
                  {(c.authorId === user?.id || isOwn) && (
                    <button onClick={() => deleteComment(c.id)} className="opacity-0 group-hover:opacity-100 text-surface-500 hover:text-red-400 shrink-0">
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
              <div ref={commentEndRef} />
            </div>
            <div className="flex items-center gap-2 px-4 py-3 border-t border-surface-700">
              <input
                className="flex-1 bg-surface-800 text-surface-100 text-sm rounded-full px-4 py-2 outline-none placeholder-surface-500"
                placeholder="Написать комментарий…"
                value={commentInput}
                onChange={(e) => setCommentInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitComment(); } }}
                autoFocus
              />
              <button onClick={submitComment} disabled={!commentInput.trim() || commentLoading} className="text-primary-400 disabled:text-surface-600">
                <Send size={20} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Share sheet ── */}
      {showShare && (
        <div className="absolute inset-0 bg-black/60 z-30 flex items-end justify-center" onClick={() => setShowShare(false)}>
          <div className="bg-surface-900 rounded-t-2xl w-full max-w-lg flex flex-col max-h-[60vh]" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-surface-700">
              <h3 className="text-surface-100 font-semibold text-sm">Переслать</h3>
              <button onClick={() => setShowShare(false)} className="text-surface-400"><X size={18} /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-2">
              {friends.length === 0 && (
                <p className="text-surface-500 text-sm text-center py-6">Нет друзей для отправки</p>
              )}
              {friends.map(({ friend }) => (
                <div key={friend.id} className="flex items-center gap-3 py-2">
                  <Avatar username={friend.username} src={friend.avatarUrl} size="sm" />
                  <div className="flex-1 min-w-0">
                    <p className="text-surface-100 text-sm">{friend.displayName}</p>
                    <p className="text-surface-500 text-xs font-mono">@{friend.username}</p>
                  </div>
                  <button
                    onClick={() => sendToDm(friend.id)}
                    className={`text-sm px-3 py-1 rounded-full transition-colors ${sentTo.has(friend.id) ? 'bg-surface-700 text-surface-400' : 'bg-primary-600 hover:bg-primary-500 text-white'}`}
                  >
                    {sentTo.has(friend.id) ? 'Отправлено' : 'Отправить'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {analyticsFor && (
        <StoryAnalyticsModal
          storyId={analyticsFor}
          onClose={() => setAnalyticsFor(null)}
        />
      )}
    </div>
  );
}
